// eslint-disable-next-line @mews/no-restricted-imports-overrides
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { notFound, RegisteredRouter, RouterProvider } from '@tanstack/react-router';
import { hasPlatformPrivilege, hasPrivilege, PlatformPrivilege, Privilege } from '@mews-commander/core';
import { EnsureRepositoryQueryData, useAppConfiguration } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { ApplicationState, CommanderThunkDispatch } from '@mews-commander/types';
import { useCommanderThunkDispatch } from '@mews-commander/utils';
import { PlatformEnvironment } from '@mews/enums';
import {
    DateTimeLocalizationInfo,
    FeatureFlagService,
    LocalizeTextProps,
    useDateTimeLocalizationInfo,
    useFeatureFlagService,
    useLocalizeProperty,
    useLocalizeText,
    useTimezone,
} from '@mews/framework';

type LocalizeProperty = ReturnType<typeof useLocalizeProperty>;

export interface RouterContext {
    /** @deprecated DO NOT USE dispatch IN ROUTER UNDER ANY CIRCUMSTANCES!!! */
    dispatch__DO_NOT_USE_OR_YOU_WILL_BE_FIRED: CommanderThunkDispatch;
    /** @deprecated DO NOT USE getState IN ROUTER UNDER ANY CIRCUMSTANCES!!! */
    getState__DO_NOT_USE_OR_YOU_WILL_BE_FIRED: () => ApplicationState;
    featureFlagService: FeatureFlagService;
    globalization: {
        timezone: string;
        dateTimeLocalizationInfo: DateTimeLocalizationInfo;
    };
    environment: PlatformEnvironment;
    /** @deprecated Use component logic or ensureQueryData instead */
    queryClient__DO_NOT_USE: QueryClient;
    ensureQueryData: EnsureRepositoryQueryData;
    localizeText: ReturnType<typeof useLocalizeText>;
    localizeProperty: (property: Parameters<LocalizeProperty>[0]) => ReturnType<LocalizeProperty>;
    getTitle?: () => LocalizeTextProps;
    checkFeatureFlag: (featureFlag: FeatureFlag) => void;
    checkPrivilege: (privilege: Privilege) => void;
    checkPlatformPrivilege: (privilege: PlatformPrivilege) => void;
}

export interface TanStackAppProps {
    getState: () => ApplicationState;
    ensureQueryData: EnsureRepositoryQueryData;
    router: RegisteredRouter;
}

export const TanStackApp = ({ getState, ensureQueryData, router }: TanStackAppProps) => {
    const dispatch = useCommanderThunkDispatch();
    const featureFlagService = useFeatureFlagService();
    const localizeProperty = useLocalizeProperty();
    const { environment } = useAppConfiguration();
    const queryClient = useQueryClient();
    const timezone = useTimezone();
    const dateTimeLocalizationInfo = useDateTimeLocalizationInfo();

    const checkFeatureFlag = (featureFlag: FeatureFlag) => {
        if (!featureFlagService.hasFlag(featureFlag)) throw notFound();
    };
    const checkPrivilege = (privilege: Privilege) => {
        if (!hasPrivilege(privilege)) throw notFound();
    };

    const checkPlatformPrivilege = (privilege: PlatformPrivilege) => {
        if (!hasPlatformPrivilege(privilege)) throw notFound();
    };

    const context: RouterContext = {
        featureFlagService,
        environment,
        queryClient__DO_NOT_USE: queryClient,
        checkFeatureFlag,
        checkPrivilege,
        globalization: {
            timezone,
            dateTimeLocalizationInfo,
        },
        checkPlatformPrivilege,
        localizeText: useLocalizeText(),
        localizeProperty: (property) => localizeProperty(property),
        dispatch__DO_NOT_USE_OR_YOU_WILL_BE_FIRED: dispatch,
        getState__DO_NOT_USE_OR_YOU_WILL_BE_FIRED: getState,
        ensureQueryData,
    };

    return <RouterProvider context={context} router={router} />;
};
